import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import { Link, navigate } from '@reach/router';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import {
    CommonContextV2,
    CommonLayoutV2,
    CustomButtonV2,
    CustomCheckboxV2,
    CustomInputV2,
    CustomPasswordMeterV2,
    CustomSelectV2,
    CustomSnackbarV2
} from 'components/v2';
import { Button } from 'react-bulma-components';

import useMARedirect, { MA_PAGE } from 'hooks/useMARedirect';

import { axios } from 'services/axios';
import {
    errorMessageMap,
    EXCLUDED_QUERY_PARAMS,
    NETWORK_ERROR_MESSAGE,
    OPSWAT_PRIVACY_POLICY_URL,
    OPSWAT_TERMS_OF_SERVICE_URL
} from 'services/Constant';
import countryData from 'services/countryData';
import leadSourcesData, { allowReferralSourceDetail } from 'services/leadSourcesData';
import { logService } from 'services/LogService';
import QueryParametersParser from 'services/QueryParametersParser';
import Validator from 'services/Validator';

import './register.scss';

const pageTitle = 'OPSWAT Accounts | Register';

const HUBSPOT_QUERY_PARAMS = [
    {
        app: process.env.REACT_APP_appID_SKL,
        allowedParams: ['academy-skilljar', 'academy-hubsspotlp', null]
    }
];

const REGISTER_FIELDS = [
    {
        label: 'First Name',
        name: 'firstName',
        type: 'text',
        isRequired: true,
        validation: (value) => Validator.checkName(value)
    },
    {
        label: 'Last Name',
        name: 'lastName',
        type: 'text',
        isRequired: true,
        validation: (value) => Validator.checkName(value)
    },
    {
        label: 'Email',
        name: 'email',
        type: 'email',
        isRequired: true,
        validation: (value) => {
            let errors = [];
            if (!Validator.isEmail(value)) {
                return [...errors, 'Invalid Email Address'];
            }

            return errors;
        }
    },
    {
        label: 'Password',
        name: 'password',
        type: 'password',
        isRequired: true,
        showPasswordMeter: false,
        validation: (value) => Validator.checkPassword(value)
    },
    {
        label: 'Confirm Password',
        name: 'passwordConfirmation',
        type: 'password',
        isRequired: true,
        validation: (value, passwordValue) => {
            let errors = [];
            if (value !== passwordValue) {
                errors.push('Passwords do not match');
            }

            return errors || null;
        }
    },
    {
        label: 'Company Name',
        name: 'companyName',
        type: 'text',
        isRequired: true,
        validation: (value) => (value ? Validator.checkCompany(value) : [])
    },
    {
        label: 'Country',
        name: 'country',
        type: 'select',
        multiple: false,
        isRequired: true,
        validation: (value) => (value ? Validator.checkCountry(value) : []),
        options: countryData.countryList
    },
    {
        label: 'How did you hear about OPSWAT? Check all that apply',
        name: 'referralSources',
        type: 'select',
        multiple: true,
        isRequired: true,
        validation: () => [],
        options: leadSourcesData.leadSourcesList,
        inputClass: 'referral_input'
    },
    {
        label: 'Please provide more detail about your choice',
        name: 'referralSourceDetail',
        type: 'text',
        isRequired: true,
        validation: (value) => (value ? Validator.checkReferralSourceDetail(value) : [])
    }
];

const useIsMountedRef = () => {
    const isMountedRef = useRef(null);
    useEffect(() => {
        isMountedRef.current = true;
        return () => (isMountedRef.current = false);
    });
    return isMountedRef;
};

const RegisterPageV2 = ({ location }) => {
    const { query } = useContext(CommonContextV2);

    const [fieldData, setFieldData] = useState({
        email: '',
        password: '',
        passwordConfirmation: '',
        firstName: '',
        lastName: '',
        companyName: '',
        country: '',
        emailCheckbox: true,
        referralSources: [],
        referralSourceDetail: ''
    });
    const [consentAgree, setConsentAgree] = useState(false);
    const [captchaResponse, setCaptchaResponse] = useState();
    const recaptchaRef = useRef(null);
    const isMountedRef = useIsMountedRef();
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [showReferralSourceDetail, setShowReferralSourceDetail] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({
        email: [],
        password: [],
        passwordConfirmation: [],
        firstName: [],
        lastName: [],
        companyName: [],
        country: [],
        referralSources: [],
        referralSourceDetail: []
    });
    const [currentStep, setCurrentStep] = useState(1);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [animationClass, setAnimationClass] = useState('');

    const isInQA = useMemo(
        () =>
            query?.inQA === 'true' &&
            (process.env.REACT_APP_ENVIRONMENT === 'dev' || process.env.REACT_APP_ENVIRONMENT === 'qa'),
        [query]
    );

    const onChange = useCallback(
        (evt) => {
            setErrors([]);
            const value = evt.target.type === 'checkbox' ? evt.target.checked : evt.target.value;
            const currentInput = REGISTER_FIELDS.filter((field) => field.name === evt.target.name)[0];
            const passwordConfirmationInput = REGISTER_FIELDS.filter(
                (field) => field.name === 'passwordConfirmation'
            )[0];
            const isPasswordConfirmation = evt.target.name === 'passwordConfirmation';
            /* eslint-disable indent */
            const currentInputErrors = !isPasswordConfirmation
                ? currentInput?.validation(value)
                : passwordConfirmationInput?.validation(value, fieldData['password']);
            const passwordConfirmationErrors =
                evt.target.name === 'password' && fieldData['passwordConfirmation'].length
                    ? passwordConfirmationInput?.validation(fieldData['passwordConfirmation'], value)
                    : isPasswordConfirmation
                      ? currentInputErrors
                      : fieldErrors['passwordConfirmation'];
            /* eslint-disable indent */

            if (evt.target.type !== 'checkbox') {
                setFieldErrors({
                    ...fieldErrors,
                    [evt.target.name]: currentInputErrors,
                    passwordConfirmation: passwordConfirmationErrors
                });
            }
            if (evt.target.name === 'referralSources') {
                const referralSources = evt.target.value;
                const referralSourcesCodes = referralSources.map((referralSource) => referralSource.code);
                const shouldShowDetail = referralSourcesCodes.some((element) =>
                    allowReferralSourceDetail.includes(element)
                );
                setShowReferralSourceDetail(shouldShowDetail);
            }

            setFieldData({ ...fieldData, [evt.target.name]: value });
        },
        [fieldData, fieldErrors]
    );

    useEffect(() => {
        if (!showReferralSourceDetail) {
            setFieldData((prevData) => ({
                ...prevData,
                referralSourceDetail: ''
            }));
        }
    }, [showReferralSourceDetail]);

    const isValid = useMemo(() => {
        const isError =
            Object.entries(errors).filter((error) => error[1].length !== 0) &&
            Object.entries(fieldErrors).filter((error) => error[1]?.length !== 0);
        if (currentStep === 1) {
            const { email, password, passwordConfirmation, firstName, lastName } = fieldData;
            return email && password && passwordConfirmation && firstName && lastName && !isError.length;
        } else if (!isError.length) {
            const { companyName, country, referralSources, referralSourceDetail } = fieldData;
            const referralSourcesCodes = referralSources.map(({ code }) => code);
            const hasReferralSources = referralSources.length > 0;
            const requiresDetail = referralSourcesCodes.some((code) => allowReferralSourceDetail.includes(code));
            const isValidReferralSources = hasReferralSources && (requiresDetail ? referralSourceDetail?.trim() : true);
            return companyName && country && isValidReferralSources && consentAgree && (captchaResponse || isInQA);
        }
        return false;
    }, [fieldData, consentAgree, errors, fieldErrors, captchaResponse, isInQA, currentStep]);

    const onSubmit = useCallback(
        (e) => {
            e && e.preventDefault();
            if (!isValid) {
                return;
            }

            setIsLoading(true);
            setErrors([]);

            const {
                email,
                password,
                firstName,
                lastName,
                companyName,
                referralSources,
                referralSourceDetail,
                emailCheckbox,
                country
            } = fieldData;
            (async () => {
                try {
                    const leadSourceParam = query?.leadsource?.toLowerCase()?.trim() || null;
                    const isLeadSourceParamAllowed = HUBSPOT_QUERY_PARAMS.filter(
                        (param) => param.app === query?.app && param.allowedParams.includes(leadSourceParam)
                    ).length
                        ? true
                        : false;

                    const cleanedQuery = { ...query };
                    EXCLUDED_QUERY_PARAMS.forEach((field) => delete cleanedQuery[field]);

                    const data = {
                        email,
                        password,
                        firstName,
                        lastName,
                        companyName,
                        referralSources,
                        referralSourceDetail,
                        country: _get(country, 'name', ''),
                        isConsent: consentAgree,
                        emailRegister: emailCheckbox,
                        captchaToken: captchaResponse,
                        query: cleanedQuery,
                        leadSourceParam: isLeadSourceParamAllowed ? leadSourceParam : undefined
                    };

                    const request = await axios.post('/register' + (isInQA ? '?inQA=true' : ''), data);

                    if (request.status === 200) {
                        await axios.get('/logout');
                        // Thank form
                        await navigate('/confirmEmail' + QueryParametersParser.toString(query), { state: { email } });
                    }
                } catch (err) {
                    logService.error(err);
                    let messages, title;
                    if (
                        (err?.response?.status >= 400 && err?.response?.status < 500) ||
                        errorMessageMap(err?.response?.data?.error?.code)
                    ) {
                        title = 'Error';
                        messages = ['Your register attempt has failed. Please try again.'];
                    } else {
                        title = 'Error';
                        messages = [NETWORK_ERROR_MESSAGE];
                    }

                    messages.push(errorMessageMap(err?.response?.data?.error?.code));

                    setErrors([
                        {
                            title,
                            error: err,
                            messages
                        }
                    ]);
                }

                if (recaptchaRef?.current) {
                    recaptchaRef.current.reset();
                }

                if (isMountedRef?.current) {
                    setCaptchaResponse(undefined);
                    setIsLoading(false);
                }
            })();
        },
        [captchaResponse, consentAgree, fieldData, isInQA, isValid, query]
    );

    const handleStepChange = useCallback(
        (newStep) => {
            if (isTransitioning) {
                return;
            }

            setIsTransitioning(true);
            const direction = newStep > currentStep ? 'forward' : 'backward';
            const outClass = direction === 'forward' ? 'slide-out-left' : 'slide-out-right';
            const inClass = direction === 'forward' ? 'slide-in-right' : 'slide-in-left';

            setAnimationClass(`register__form-step--${outClass}`);

            setTimeout(() => {
                setCurrentStep(newStep);
                setAnimationClass(`register__form-step--${inClass}`);

                setTimeout(() => {
                    setIsTransitioning(false);
                    setAnimationClass('');
                }, 300);
            }, 300);
        },
        [currentStep, isTransitioning]
    );

    const registerFormDom = useMemo(
        () => (
            <div className='register__form'>
                <div className='register__form-header'>
                    {currentStep === 2 && (
                        <Button className='back-button' onClick={() => handleStepChange(1)}>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='16'
                                height='16'
                                viewBox='0 0 12 10'
                                fill='none'
                            >
                                <path
                                    d='M5 1L1 5L5 9'
                                    stroke='#0C121D'
                                    strokeWidth='1.5'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                                <path
                                    d='M1 5H11'
                                    stroke='#0C121D'
                                    strokeWidth='1.5'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                        </Button>
                    )}
                    <h3>Create Account</h3>
                </div>
                {currentStep === 1 && (
                    <div className={`register__form-step ${animationClass}`}>
                        <CustomInputV2
                            required
                            id='email'
                            name='email'
                            type='text'
                            label='Email'
                            placeholder='Enter your email'
                            onChange={onChange}
                            value={fieldData.email}
                            errors={fieldErrors.email}
                            autoComplete='off'
                        />
                        <div className='register__form-row custom-input__container'>
                            <div className='register__form-column'>
                                <CustomInputV2
                                    required
                                    id='firstName'
                                    name='firstName'
                                    type='text'
                                    label='First Name'
                                    placeholder='First Name'
                                    onChange={onChange}
                                    value={fieldData.firstName}
                                    errors={fieldErrors.firstName}
                                />
                            </div>
                            <div className='register__form-column'>
                                <CustomInputV2
                                    required
                                    id='lastName'
                                    name='lastName'
                                    type='text'
                                    label='Last Name'
                                    placeholder='Last Name'
                                    onChange={onChange}
                                    value={fieldData.lastName}
                                    errors={fieldErrors.lastName}
                                />
                            </div>
                        </div>
                        {/* dummy inputs to prevent autofill */}
                        <input
                            className='register__input-dummy'
                            aria-hidden='true'
                            tabIndex={-1}
                            name='fake-lastname'
                            autoComplete='new-lastname'
                        />
                        <input
                            className='register__input-dummy'
                            aria-hidden='true'
                            tabIndex={-1}
                            type='password'
                            name='fake-password'
                            autoComplete='new-password'
                        />
                        <CustomInputV2
                            required
                            id='password'
                            name='password'
                            type='password'
                            label='Password'
                            placeholder='Enter password'
                            onChange={onChange}
                            value={fieldData.password}
                            errors={fieldErrors.password}
                            rightLabel={<CustomPasswordMeterV2 value={fieldData.password} />}
                        />
                        <CustomInputV2
                            required
                            id='passwordConfirmation'
                            name='passwordConfirmation'
                            type='password'
                            label='Confirm Password'
                            placeholder='Confirm password'
                            onChange={onChange}
                            value={fieldData.passwordConfirmation}
                            errors={fieldErrors.passwordConfirmation}
                        />
                    </div>
                )}
                {currentStep === 2 && (
                    <div
                        className={`register__form-step ${animationClass}`}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '24px'
                        }}
                    >
                        <div>
                            <CustomInputV2
                                required
                                id='companyName'
                                name='companyName'
                                type='text'
                                label='Company Name'
                                placeholder='Company Name'
                                onChange={onChange}
                                value={fieldData.companyName}
                                errors={fieldErrors.companyName}
                            />
                            <CustomSelectV2
                                required
                                multiple={false}
                                label='Country'
                                name='country'
                                options={countryData.countryList}
                                onChange={onChange}
                                value={fieldData.country}
                                errors={fieldErrors.country}
                                placeholder='Select country'
                            />
                            <CustomSelectV2
                                required
                                multiple={true}
                                label='How did you hear about OPSWAT?'
                                name='referralSources'
                                options={leadSourcesData.leadSourcesList}
                                onChange={onChange}
                                value={fieldData.referralSources}
                                errors={fieldErrors.referralSources}
                                placeholder='Select all that apply'
                            />
                            {showReferralSourceDetail && (
                                <CustomInputV2
                                    required
                                    id='referralSourceDetail'
                                    name='referralSourceDetail'
                                    type='text'
                                    label='Please provide more detail about your choice'
                                    placeholder='Event name, pattern name...'
                                    onChange={onChange}
                                    value={fieldData.referralSourceDetail}
                                    errors={fieldErrors.referralSourceDetail}
                                />
                            )}
                        </div>
                        <div className='register__form-consent'>
                            <CustomCheckboxV2
                                required
                                label={
                                    <span>
                                        I agree to OPSWAT Inc.'s{' '}
                                        <a href={OPSWAT_TERMS_OF_SERVICE_URL} target='_blank' rel='noopener noreferrer'>
                                            Terms of Service
                                        </a>{' '}
                                        and{' '}
                                        <a href={OPSWAT_PRIVACY_POLICY_URL} target='_blank' rel='noopener noreferrer'>
                                            Privacy Policy
                                        </a>
                                    </span>
                                }
                                name='isConsent'
                                checked={!!consentAgree}
                                onChange={() => setConsentAgree(true)}
                            />

                            <CustomCheckboxV2
                                label={
                                    <span>
                                        Subscribe to OPSWAT email communications{' '}
                                        <span
                                            style={{
                                                color: '#344565'
                                            }}
                                        >
                                            (Optional)
                                        </span>
                                    </span>
                                }
                                name='emailCheckbox'
                                checked={fieldData.emailCheckbox}
                                onChange={onChange}
                            />
                        </div>
                        <div className='register__form-captcha'>
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={process.env.REACT_APP_GRC_SITE_KEY}
                                onChange={(response) => setCaptchaResponse(response)}
                            />
                        </div>
                    </div>
                )}
                <CustomButtonV2
                    disabled={!isValid || isLoading || isTransitioning}
                    loading={isLoading}
                    onClick={(e) => {
                        if (currentStep === 1) {
                            handleStepChange(2);
                        } else {
                            onSubmit(e);
                        }
                    }}
                >
                    {currentStep === 1 ? 'Continue' : 'Register'}
                </CustomButtonV2>
                {!_isEmpty(errors) && <CustomSnackbarV2 messageList={errors} />}
                <div className='register__footer'>
                    <span>Already have an account?</span>
                    <Link to={`/login${location.search || ''}`}>Sign in</Link>
                </div>
            </div>
        ),
        [
            fieldData,
            fieldErrors,
            isLoading,
            isValid,
            currentStep,
            onChange,
            onSubmit,
            location,
            animationClass,
            handleStepChange,
            isTransitioning
        ]
    );

    if (useMARedirect(location?.search, MA_PAGE.REGISTER)) {
        return null;
    }

    return (
        <CommonLayoutV2
            pageTitle={pageTitle}
            isLoading={false}
            contentStyles={{
                gap: '40px'
            }}
        >
            <div className='register__container'>{registerFormDom}</div>
        </CommonLayoutV2>
    );
};

export default RegisterPageV2;

RegisterPageV2.propTypes = {
    location: PropTypes.object
};
